import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverTrigger, PopoverContent } from "@workspace/ui/components/ui/popover";
import { AvailabilityCalendar } from "@/components/cars/AvailabilityCalendar";
import { TimePicker } from "@/components/ui/time-picker";
import { Spinner } from "@/components/ui/spinner";
import { calculateChargeableDays } from "@/lib/days";
import { cn } from "@/lib/utils";
import type { AvailabilityData } from "@/lib/api";
import { parseBookingTime } from "@/lib/booking-time";

type RentalType = "driving" | "chauffeured" | "event";

export interface CheckoutTripDraft {
  dateFrom: string;
  dateTo: string;
  pickupTime: string;
  dropoffTime: string;
  hours: number | undefined;
}

function timeToHours(timeStr: string): number {
  const parsed = parseBookingTime(timeStr);
  return parsed ? parsed.hour24 + parsed.minute / 60 : Number.NaN;
}

interface Props {
  trigger: React.ReactNode;
  rentalType: RentalType;
  dateFrom: string;
  dateTo: string;
  pickupTime: string;
  dropoffTime: string;
  hours: number | undefined;
  availability: AvailabilityData | null;
  loadingAvailability: boolean;
  onApply: (next: CheckoutTripDraft) => void;
  // Task #2655 — opt-in for the host-side RequestChangeModal: dismissing
  // the popover with staged changes auto-applies them. Off by default so
  // the renter checkout flow and other call sites keep their original
  // explicit-Apply semantics.
  autoApplyOnDismiss?: boolean;
  // Task #2940 follow-up — in-progress trips lock the pickup half of the
  // editor. The calendar still renders both months and the start pill,
  // but clicks before the locked anchor (and the start TimePicker) are
  // disabled. The submit handler still forwards the locked values
  // verbatim so the consumer doesn't need to splice them back in.
  lockStart?: boolean;
}

export function CheckoutDatesEditor({
  trigger,
  rentalType,
  dateFrom,
  dateTo,
  pickupTime,
  dropoffTime,
  hours,
  availability,
  loadingAvailability,
  onApply,
  autoApplyOnDismiss = false,
  lockStart = false,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(dateFrom);
  const [to, setTo] = useState(dateTo);
  const [pickup, setPickup] = useState(pickupTime);
  const [dropoff, setDropoff] = useState(dropoffTime);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFrom(dateFrom);
      setTo(dateTo);
      setPickup(pickupTime);
      setDropoff(dropoffTime);
      setError(null);
    }
  }, [open, dateFrom, dateTo, pickupTime, dropoffTime, hours]);

  const isHourly = rentalType === "chauffeured" || rentalType === "event";
  // Task #2751 — event rentals are billed by true 24-hour periods on the
  // server (from pickupTime/dropoffTime), so they don't need a derived
  // `hours` value at all. Chauffeur is still hourly-per-day, so it
  // continues to drive the half-hour-snapped `hours` field.
  const needsDerivedHours = rentalType === "chauffeured";
  const minDays = isHourly ? 1 : (availability?.window?.min_booking_days || 1);

  // Chauffeured is a single-day service — keep the staged drop-off date
  // locked to the pick-up date. The shared calendar enforces this on click;
  // this also collapses a range that arrives from an existing (e.g. legacy
  // cross-midnight) trip when the editor opens.
  useEffect(() => {
    if (needsDerivedHours && from && to !== from) {
      setTo(from);
    }
  }, [needsDerivedHours, from, to]);

  // Task #2655 — quietly commit the staged dates/times when the popover
  // dismisses without an explicit Apply. Returns true when a commit
  // actually happened so the popover's onOpenChange knows whether to
  // skip its own setOpen(false) (handleApply already does it). Validation
  // failures fall through silently — the staged values just get
  // discarded the same way they did before this hook existed.
  const buildApplyPayload = (): CheckoutTripDraft | null => {
    if (!from || !to) return null;
    const days = calculateChargeableDays(from, to);
    if (days < minDays) return null;
    // Same-day self-drive / event: drop-off must come after pick-up. A
    // multi-day span with an earlier clock-time return is a legitimate
    // overnight rental, so only enforce ordering when both dates match.
    // Chauffeur is handled by the derived-hours span check below.
    if (!needsDerivedHours && from === to) {
      const sameDaySpan = timeToHours(dropoff) - timeToHours(pickup);
      if (!Number.isFinite(sameDaySpan) || sameDaySpan <= 0) return null;
    }
    let derivedHours: number | undefined;
    if (needsDerivedHours) {
      const span = timeToHours(dropoff) - timeToHours(pickup);
      if (!Number.isFinite(span) || span <= 0 || span > 24) return null;
      derivedHours = Math.max(Math.ceil(span * 2) / 2, 1);
    }
    const payload: CheckoutTripDraft = { dateFrom: from, dateTo: to, pickupTime: pickup, dropoffTime: dropoff, hours: derivedHours };
    return payload;
  };

  const stagedChanged = from !== dateFrom || to !== dateTo || pickup !== pickupTime || dropoff !== dropoffTime;

  const handleOpenChange = (next: boolean) => {
    if (!next && open && stagedChanged && autoApplyOnDismiss) {
      const payload = buildApplyPayload();
      if (payload) {
        onApply(payload);
      }
    }
    setOpen(next);
  };

  const handleApply = () => {
    if (!from || !to) {
      setError(t("checkout.edit_dates_pick_both", "Please pick both dates."));
      return;
    }
    const days = calculateChargeableDays(from, to);
    if (days < minDays) {
      setError(
        t("checkout.edit_dates_min_days", "Minimum booking is {{n}} day(s).", { n: minDays }),
      );
      return;
    }
    // Same-day self-drive / event: drop-off must come after pick-up. The
    // chauffeur branch below enforces this via its derived-hours span
    // check; multi-day overnight returns at an earlier clock time stay
    // valid, so only gate when both dates match.
    if (!needsDerivedHours && from === to) {
      const sameDaySpan = timeToHours(dropoff) - timeToHours(pickup);
      if (!Number.isFinite(sameDaySpan) || sameDaySpan <= 0) {
        setError(
          t("booking.invalid_time_range", "Drop-off must be after pick-up."),
        );
        return;
      }
    }
    // For chauffeur trips, derive `hours` from the start/end TimePicker pair
    // (mirrors BookingWidget's `quantizeHours` so the editor and the original
    // booking widget agree on duration to the half-hour). Event trips skip
    // this — the server uses pickupTime/dropoffTime to compute true 24-hour
    // billing (task #2751), so the renter is free to pick a multi-day span
    // here and the chauffeur-specific same-day-and-≤24h validation no
    // longer applies to events.
    let derivedHours: number | undefined;
    if (needsDerivedHours) {
      const span = timeToHours(dropoff) - timeToHours(pickup);
      // Validate the raw span, not the post-max derived hours: the
      // Math.max(...,1) clamp would silently turn a negative span (e.g.
      // pickup 8 pm, drop-off 6 am) into a 1-hour booking. Chauffeur
      // trips are by definition single-day, so insist drop-off comes
      // strictly after pick-up and within 24 hours.
      if (!Number.isFinite(span) || span <= 0 || span > 24) {
        setError(
          t(
            "checkout.edit_dates_invalid_time_range",
            "Drop-off must be after pick-up, within 24 hours.",
          ),
        );
        return;
      }
      derivedHours = Math.max(Math.ceil(span * 2) / 2, 1);
    }
    onApply({
      dateFrom: from,
      dateTo: to,
      pickupTime: pickup,
      dropoffTime: dropoff,
      hours: derivedHours,
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={16}
        // Cap height to the actual gap between the trigger and the
        // viewport edge that Radix can find (after side-flip / shift),
        // not the full viewport. Using `100vh` here was the bug: when
        // the trigger sat mid-page inside the parent Request-a-change
        // modal, the popover could still be ~viewport-tall and its
        // bottom (the Apply footer + last calendar rows) fell below
        // the visible viewport with no way to scroll past — the body
        // has `overflow-y-auto` but only inside whatever height the
        // popover container actually gets. `--radix-popover-content-
        // available-height` is Radix's computed safe height.
        style={{ maxHeight: "min(calc(100vh - 32px), var(--radix-popover-content-available-height))" }}
        // Task #2679 — the parent RequestChangeModal is a `fixed inset-0
        // z-50` div, and `components/ui/popover.tsx` defaults to `z-50`
        // too. When this editor opens from inside that modal, the
        // portaled popover ends up sharing a stacking level with the
        // modal's own card / footer; the inner card's `relative`
        // positioning and shadow are enough on some browsers to let the
        // modal's sticky footer (Send button area) win the hit-test over
        // the popover footer at the same coordinates. The visible
        // symptom is that the Apply button "does nothing" — the click
        // never reaches the button because the modal element intercepts
        // it, but Radix still sees it as an outside-click and closes
        // the popover, which is why Cancel _looks_ like it works. Bump
        // the popover above the modal so clicks land on Apply.
        // `pointer-events-auto` is belt-and-braces against a leftover
        // pointer-events-none layer from a previously-closed inner modal.
        className="z-[60] pointer-events-auto flex w-[min(96vw,640px)] max-w-[96vw] flex-col overflow-hidden rounded-2xl border border-border bg-popover p-0 shadow-xl"
      >
        <div className="shrink-0 border-b border-border px-5 py-4">
          <p className="text-base font-semibold text-foreground">
            {t("checkout.edit_dates_title", "Edit dates")}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("checkout.edit_dates_subtitle", "Pick new dates and times for your trip.")}
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {loadingAvailability ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Spinner size="md" />
            </div>
          ) : availability ? (
            <AvailabilityCalendar
              availability={availability}
              selectedFrom={from}
              selectedTo={to}
              rentalType={rentalType}
              lockFrom={lockStart}
              onDateSelect={(f, tt) => {
                setError(null);
                // Task #2940 follow-up — in lockStart mode the calendar
                // shouldn't be able to move the anchor; clamp defensively.
                setFrom(lockStart ? dateFrom : f);
                setTo(tt);
              }}
            />
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t("checkout.edit_dates_no_availability", "Availability data unavailable. Please try again.")}
            </p>
          )}

          {/* Pick-up / drop-off TimePickers — same UI for self-drive and
              hourly (chauffeured/event). For hourly trips we used to
              ask for a raw hour count, but the original BookingWidget
              on the listing page collects start/end times instead;
              renters expect to edit the same inputs they originally
              chose. The duration is derived in handleApply. */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {t("booking.pick_up", "Pick-up")}
              </p>
              <div className={cn("mt-1.5", lockStart && "pointer-events-none opacity-60")}>
                <TimePicker
                  label={t("booking.pick_up", "Pick-up")}
                  value={pickup}
                  onChange={(v) => {
                    if (lockStart) return;
                    setError(null);
                    setPickup(v);
                  }}
                  disabled={lockStart}
                  size="sm"
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {t("booking.drop_off", "Drop-off")}
              </p>
              <div className="mt-1.5">
                <TimePicker
                  label={t("booking.drop_off", "Drop-off")}
                  value={dropoff}
                  onChange={(v) => {
                    setError(null);
                    setDropoff(v);
                  }}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {error && (
            <p className="mt-3 text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="cursor-pointer rounded-md bg-foreground px-4 py-1.5 text-sm font-semibold text-background hover:bg-foreground/90"
          >
            {t("common.apply", "Apply")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default CheckoutDatesEditor;
